import React, { useState, useEffect } from 'react';
import {
  Modal,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { formatCreatedDate } from '../constants/utils';
import api from '../helpers/api';
import { WorkOrdersTable } from './Common';

const CategoryModal = ({ isOpen, toggle, categoryData, loading, onEditCategory = null, onTicketClick = null }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('info');
  const [workOrders, setWorkOrders] = useState([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(false);

  // Fetch work orders using this category
  const fetchWorkOrders = async () => {
    if (!categoryData?.id) return;

    setWorkOrdersLoading(true);
    try {
      const { data } = await api.get(`organization-category/get-all/work-orders?categoryId=${categoryData.id}`);
      // Extract work orders from the response
      const allWorkOrders = data?.response || [];
      setWorkOrders(allWorkOrders);
    } catch (error) {
      console.error('Error fetching work orders:', error);
      setWorkOrders([]);
    } finally {
      setWorkOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'workorders' && categoryData?.id) {
      fetchWorkOrders();
    }
  }, [activeTab, categoryData?.id]);

  // Reset tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('info');
    }
  }, [isOpen]);

  if (!categoryData && !loading) return null;

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Enhanced Header */}
        <div
          className="modal-header border-0 bg-gradient-primary text-white position-sticky"
          style={{
            top: 0,
            zIndex: 10,
            background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
            padding: '1.5rem'
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-category text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">
                {categoryData?.name || t('business.components.category_modal.title_default', 'Category Details')}
              </h5>
              <p className="text-white-50 small mb-0">{t('business.components.category_modal.subtitle', 'Category information and details')}</p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 ms-auto">
            {onEditCategory && categoryData && (
              <button
                type="button"
                className="btn btn-sm d-flex align-items-center gap-2 px-3 py-2 border-0 shadow-sm"
                onClick={() => onEditCategory(categoryData)}
                title={t('shared.edit_category', 'Edit Category')}
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  color: '#495057',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  borderRadius: '8px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                }}
              >
                <FeatherIcon icon="edit-3" size={16} />
                <span>{t('shared.edit_category', 'Edit Category')}</span>
              </button>
            )}
            <button
              onClick={toggle}
              type="button"
              className="btn-close btn-close-white"
              aria-label={t('shared.close', 'Close')}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <Nav
          tabs
          className="border-bottom-0 px-4 pt-3 bg-light"
          style={{ borderRadius: '0' }}
        >
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-info-circle me-2"></i>
              {t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'workorders' ? 'active' : ''}`}
              onClick={() => setActiveTab('workorders')}
              style={{
                border: 'none',
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'workorders' ? '#fff' : 'transparent',
                color: activeTab === 'workorders' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'workorders' ? '600' : '400',
                cursor: 'pointer'
              }}
            >
              <i className="bx bx-food-menu me-2"></i>
              {t('shared.work_orders', 'Work Orders')}
            </NavLink>
          </NavItem>
        </Nav>

        {/* Modal Body */}
        <div
          className="modal-body p-0"
          style={{
            maxHeight: 'calc(90vh - 200px)',
            overflow: 'auto'
          }}
        >
          <TabContent activeTab={activeTab} className="h-100">
            {/* Info Tab */}
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                             style={{ width: '40px', height: '40px' }}>
                          <i className="bx bx-category text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t('business.components.category_modal.info_title', 'Category Information')}</h5>
                        <p className="text-muted small mb-0">{t('business.components.category_modal.info_subtitle', 'Complete category details')}</p>
                      </div>
                    </div>

                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">{t('business.components.category_modal.loading_details', 'Loading category details...')}</p>
                      </div>
                    ) : (
                      categoryData && (
                        <Row className="g-4">
                          {/* Basic Information */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-info-circle me-2"></i>{t('shared.basic_information', 'Basic Information')}
                              </h6>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('shared.name', 'Name')}</label>
                                <div className="fw-medium">{categoryData?.name || 'N/A'}</div>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">{t('shared.description', 'Description')}</label>
                                <div className="fw-medium">{categoryData?.description || t('shared.no_description', 'No description')}</div>
                              </div>
                            </div>
                          </Col>

                          {/* Activity */}
                          <Col md={6}>
                            <div className="info-card bg-light rounded p-3 h-100">
                              <h6 className="text-primary mb-3 fw-semibold">
                                <i className="bx bx-time me-2"></i>{t('shared.activity', 'Activity')}
                              </h6>
                              {categoryData?.createdAt && (
                                <div className="info-item mb-3">
                                  <div className="fw-medium text-muted">
                                    {t('shared.created_by_on', 'Created {{user}} on {{date}}', { 
                                      user: categoryData?.createdByUser?.name ? `${t('shared.by', 'By')} ${categoryData.createdByUser.name} ` : '',
                                      date: formatCreatedDate(categoryData?.createdAt)
                                    })}
                                  </div>
                                </div>
                              )}
                              {categoryData?.updatedAt && categoryData?.createdAt !== categoryData?.updatedAt && (
                                <div className="info-item">
                                  <div className="fw-medium text-muted">
                                    {t('shared.last_updated_by_on', 'Last updated {{user}} on {{date}}', {
                                      user: categoryData?.updatedByUser?.name ? `${t('shared.by', 'By')} ${categoryData.updatedByUser.name} ` : '',
                                      date: formatCreatedDate(categoryData?.updatedAt)
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row>
                      )
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Work Orders Tab */}
            <TabPane tabId="workorders">
              <div className="p-4">
                {workOrdersLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" size="lg" />
                    <p className="text-muted mt-3">{t('business.components.category_modal.loading_work_orders', 'Loading work orders...')}</p>
                  </div>
                ) : workOrders.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="bx bx-food-menu text-muted" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h5 className="text-muted mb-2">{t('business.components.category_modal.no_work_orders', 'No work orders yet')}</h5>
                    <p className="text-muted mb-4">
                      {t('business.components.category_modal.no_work_orders_desc', "This category hasn't been assigned to any work orders yet.")}
                    </p>
                    <div className="alert alert-light border d-inline-block text-start">
                      <div className="d-flex align-items-start">
                        <i className="bx bx-bulb text-warning me-2 mt-1"></i>
                        <div>
                          <strong>{t('business.components.category_modal.how_to_use', 'How to use this category:')}</strong>
                          <ol className="mb-0 mt-2 ps-3">
                            <li>{t('business.components.category_modal.how_to_use_step_1', 'Go to Work Orders')}</li>
                            <li>{t('business.components.category_modal.how_to_use_step_2', 'Create a new work order or edit an existing one')}</li>
                            <li>{t('business.components.category_modal.how_to_use_step_3', 'Select "{{name}}" from the Category dropdown', { 
                              name: categoryData?.name || ''
                            })}</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <WorkOrdersTable
                    data={workOrders}
                    columns={['ticketNumber', 'title', 'status', 'priority', 'createdAt']}
                    onTicketClick={onTicketClick}
                    emptyMessage={t('shared.no_work_orders_found', 'No work orders found')}
                    animated={false}
                  />
                )}
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer border-0 pt-0">
          <button type="button" className="btn btn-secondary" onClick={toggle}>
            {t('shared.close', 'Close')}
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default CategoryModal;
